import { BottomGarmentIcon, PersonIcon, TopGarmentIcon } from '@/components/selectorIcons';
import type { Audience, Category } from '@/types/domain';

interface Props {
  audience: Audience;
  category: Category;
  onAudienceChange: (a: Audience) => void;
  onCategoryChange: (c: Category) => void;
  /** Which block(s) to render — defaults to both (Brand vs. Brand's usage). The Dashboard renders these as two separate numbered steps, so it calls this twice with a single section each. */
  sections?: Array<'audience' | 'category'>;
  /** Category button labels default to the fuller "Top Half"/"Bottom Half"; the Dashboard uses the shorter "Tops"/"Bottoms" to match its reference layout. */
  categoryLabels?: Record<Category, string>;
}

const AUDIENCE_OPTIONS: { value: Audience; label: string }[] = [
  { value: 'women', label: 'Women' },
  { value: 'men', label: 'Men' },
  { value: 'child', label: 'Youth (under 12)' },
];

const DEFAULT_CATEGORY_LABELS: Record<Category, string> = {
  tops: 'Top Half',
  bottoms: 'Bottom Half',
};

const CATEGORY_ORDER: Category[] = ['tops', 'bottoms'];

export function AudienceCategorySelector({
  audience,
  category,
  onAudienceChange,
  onCategoryChange,
  sections = ['audience', 'category'],
  categoryLabels = DEFAULT_CATEGORY_LABELS,
}: Props) {
  // When only one section is requested (the Dashboard's two-separate-steps
  // usage), its own numbered StepHeader already supplies this context, so
  // the inline label is redundant on screen — kept for screen readers via
  // sr-only rather than dropped, since the radiogroup's aria-label alone
  // names the control but not why it's being asked.
  const combined = sections.length > 1;

  return (
    <fieldset className="space-y-4">
      <legend className="sr-only">Who and what are you shopping for?</legend>

      {sections.includes('audience') && (
        <div>
          <span className={combined ? 'block text-sm font-medium text-ink mb-2' : 'sr-only'}>Who's shopping?</span>
          <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Audience">
            {AUDIENCE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={audience === opt.value}
                onClick={() => onAudienceChange(opt.value)}
                className={`flex min-h-[52px] items-center justify-center gap-2 rounded-xl border px-3 py-2 text-base font-medium transition-colors ${
                  audience === opt.value
                    ? 'border-brand bg-brand text-ink shadow-card'
                    : 'border-stone-200 bg-white text-ink hover:border-brand/40'
                }`}
              >
                <PersonIcon className="h-5 w-5" />
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {sections.includes('category') && (
        <div>
          <span className={combined ? 'block text-sm font-medium text-ink mb-2' : 'sr-only'}>What garment?</span>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Garment category">
            {CATEGORY_ORDER.map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={category === value}
                onClick={() => onCategoryChange(value)}
                className={`flex min-h-[52px] items-center justify-center gap-2 rounded-xl border px-3 py-2 text-base font-medium transition-colors ${
                  category === value
                    ? 'border-brand bg-brand text-ink shadow-card'
                    : 'border-stone-200 bg-white text-ink hover:border-brand/40'
                }`}
              >
                {value === 'tops' ? <TopGarmentIcon className="h-5 w-5" /> : <BottomGarmentIcon className="h-5 w-5" />}
                {categoryLabels[value]}
              </button>
            ))}
          </div>
        </div>
      )}
    </fieldset>
  );
}
